/**
 * AwarenessShellBridge — always-mounted null component in Layout.
 *
 * WHY a separate component (not merged into useEsaShellEffects):
 * - useEsaShellEffects owns the legacy ESA domain store (lastSurfaceContext).
 *   Mixing canvas awareness concerns there would violate single-responsibility
 *   and make the already-complex hook harder to reason about.
 * - This bridge writes to canvasAwarenessStore only. No ESA domain store writes.
 *
 * Responsibilities:
 * 1. Ring 1 — sync shell navigation (activeSolution, activeCanvas, etc.)
 * 2. Ring 1 — resetForSolutionSwitch when activeSolution changes
 * 3. Legacy bridge — map ESA_SURFACE_SELECTION_CHANGED selection payloads
 *    to typed Ring 2 (viewport) and Ring 3 (intent) awareness updates.
 */

import { useEffect } from 'react';
import { useShellStore } from '../../../shell/store/shellStore';
import { shellEventBus } from '../../../shell/events/eventBus';
import { useCanvasAwarenessStore } from '../store/canvasAwarenessStore';
import type {
  DataResponse,
  FilterDescriptor,
  IntentAwareness,
  SurfaceType,
} from '@growthaxl/web-foundation';

function toFilterDescriptors(raw: Array<Record<string, unknown>>): FilterDescriptor[] {
  return raw.flatMap(item => {
    if (typeof item['field'] !== 'string' || typeof item['operator'] !== 'string' || !('value' in item)) {
      return [];
    }
    return [{
      field: item['field'] as string,
      operator: item['operator'] as string,
      value: item['value'],
      ...(typeof item['label'] === 'string' ? { label: item['label'] as string } : {}),
    }];
  });
}

/**
 * Safely casts an arbitrary Record value into a DataResponse.
 * We validate minimum fields — requestId must be a string.
 */
function toDataResponse(raw: unknown): DataResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r['requestId'] !== 'string') return null;
  return {
    requestId: r['requestId'] as string,
    rows: Array.isArray(r['rows']) ? (r['rows'] as Record<string, unknown>[]) : [],
    respondedAt: typeof r['respondedAt'] === 'number' ? (r['respondedAt'] as number) : Date.now(),
  };
}

export function AwarenessShellBridge(): null {
  useEffect(() => {
    const store = useCanvasAwarenessStore;

    // ── Initial sync ──────────────────────────────────────────────────────────
    const syncFromShell = (): void => {
      const shell = useShellStore.getState();
      store.getState().setNavigation({
        solutionId: shell.activeSolution ?? undefined,
        canvasUkey: shell.activeCanvas ?? undefined,
        workspaceId: shell.activeWorkspace ?? undefined,
        activeCapability: shell.activeCapability ?? undefined,
      });
    };

    syncFromShell();

    // ── Shell store subscription (Ring 1) ─────────────────────────────────────
    const offShell = useShellStore.subscribe((state, prev) => {
      const solutionChanged = state.activeSolution !== prev.activeSolution;
      const navChanged =
        solutionChanged ||
        state.activeCanvas !== prev.activeCanvas ||
        state.activeWorkspace !== prev.activeWorkspace ||
        state.activeCapability !== prev.activeCapability;

      if (!navChanged) return;

      if (solutionChanged && state.activeSolution) {
        // Solution switch → clear Ring 2+3, update Ring 1, mark stale
        store.getState().resetForSolutionSwitch(
          state.activeSolution,
          state.activeSolution
        );
      } else {
        // Only other nav fields changed — partial update
        store.getState().setNavigation({
          solutionId: state.activeSolution ?? undefined,
          canvasUkey: state.activeCanvas ?? undefined,
          workspaceId: state.activeWorkspace ?? undefined,
          activeCapability: state.activeCapability ?? undefined,
        });
      }
    });

    // ── Legacy ESA surface selection bridge (Ring 2 + 3) ─────────────────────
    // Canvas components still use sendContext() / ESA_SURFACE_SELECTION_CHANGED.
    // Until each composed canvas migrates to publishViewport/publishIntent, we
    // map the legacy flat payload into typed awareness rings here.
    const offSurfaceSelection = shellEventBus.on('ESA_SURFACE_SELECTION_CHANGED', e => {
      const { surfaceId, surfaceType, selection } = e.payload;

      // Ring 2: surface identification + filters
      const filters = Array.isArray(selection['filters'])
        ? (selection['filters'] as Array<Record<string, unknown>>)
        : [];

      store.getState().setViewport({
        surfaceId,
        surfaceType: surfaceType as SurfaceType,
        dataContext: {
          ...useCanvasAwarenessStore.getState().viewport.dataContext,
          activeFilters: toFilterDescriptors(filters),
        },
      });

      // Ring 3: selected row IDs + active record
      const selectedRows = Array.isArray(selection['selectedRows'])
        ? (selection['selectedRows'] as string[])
        : undefined;
      const activeRecordId =
        typeof selection['activeRecordId'] === 'string'
          ? (selection['activeRecordId'] as string)
          : selectedRows?.[0];

      const intentUpdate: Partial<IntentAwareness> = {};
      if (selectedRows !== undefined) {
        intentUpdate.selectedRowIds = selectedRows;
        intentUpdate.selectedRowCount = selectedRows.length;
      }
      if (activeRecordId) {
        intentUpdate.activeRecordId = activeRecordId;
      }
      if (Object.keys(intentUpdate).length > 0) {
        store.getState().setIntent(intentUpdate);
      }

      // Ring 3: on-demand data response (UC-5) — also accept explorer keyFacts
      // as synthetic rows so converse artefacts carry Brand/Category/etc.
      const dataResponse = toDataResponse(selection['dataResponse']);
      if (dataResponse) {
        store.getState().setDataResponse(dataResponse);
      } else if (Array.isArray(selection['keyFacts'])) {
        const keyFacts = selection['keyFacts'] as Array<Record<string, unknown>>;
        const rows = keyFacts
          .filter(f => typeof f['label'] === 'string')
          .map(f => ({
            label: String(f['label']),
            value: f['value'] ?? null,
            series: 'key_facts',
          }));
        if (rows.length > 0) {
          store.getState().setDataResponse({
            requestId: `key-facts-${surfaceId || 'surface'}`,
            rows,
            respondedAt: Date.now(),
          });
        }
      }
    });

    // Pinned/active record tabs → Ring 3 so artefacts include the open record.
    const offRecordPinned = shellEventBus.on('CAPABILITY_RECORD_PINNED', e => {
      const payload = e.payload as {
        records?: Array<{ recordId?: string; label?: string }>;
        activeRecordId?: string | null;
      };
      const records = Array.isArray(payload.records) ? payload.records : [];
      store.getState().setIntent({
        pinnedRecords: records
          .filter(r => typeof r.recordId === 'string' && r.recordId.length > 0)
          .map(r => ({
            recordId: String(r.recordId),
            label: String(r.label || r.recordId),
          })),
        activeRecordId:
          typeof payload.activeRecordId === 'string' ? payload.activeRecordId : null,
      });
    });

    return () => {
      offShell();
      offSurfaceSelection();
      offRecordPinned();
    };
  }, []);

  return null;
}
